import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

/**
 * DocsNavigation — minimal top bar used on Help Center, Support, Status,
 * and Legal (Privacy/Terms) pages. Mirrors the Assistable.ai pattern:
 *   • Logo + live status badge on the left
 *   • Login link + Join Beta CTA on the right
 * No marketing-site links (Home/Pricing/Features/Compare) — those are
 * intentionally hidden so doc visitors stay focused.
 */
const DocsNavigation = () => {
  const [statusText, setStatusText] = useState("Checking services…");
  const [statusColor, setStatusColor] = useState("bg-gray-400");

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL || "";
        const res = await axios.get(`${apiBase}/api/utils/uptime`, {
          timeout: 8000,
        });
        if (cancelled) return;
        if (res.data?.all_operational) {
          setStatusText("All services operational");
          setStatusColor("bg-emerald-400");
        } else {
          setStatusText("Partial service degradation");
          setStatusColor("bg-amber-400");
        }
      } catch {
        if (cancelled) return;
        setStatusText("Status unavailable");
        setStatusColor("bg-gray-400");
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-space-900/95 backdrop-blur-xl border-b border-white/[0.06]"
      data-testid="docs-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Left: logo + live status pill */}
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/"
              className="flex items-center gap-2 flex-shrink-0"
              data-testid="docs-nav-logo"
            >
              <img
                src="/voxinity-logo.png?v=8"
                alt="Voxinity AI"
                className="h-9 md:h-10 w-auto"
              />
            </Link>

            <Link
              to="/status"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] hover:border-white/[0.12] transition-colors group"
              data-testid="docs-nav-status-pill"
            >
              <span className="relative flex h-2 w-2">
                <span
                  className={`animate-ping absolute inline-flex h-full w-full rounded-full ${statusColor} opacity-60`}
                />
                <span
                  className={`relative inline-flex rounded-full h-2 w-2 ${statusColor}`}
                />
              </span>
              <span className="text-xs text-gray-300 group-hover:text-white transition-colors truncate">
                {statusText}
              </span>
            </Link>
          </div>

          {/* Right: Login + Join Beta */}
          <div className="flex items-center gap-3 sm:gap-5 flex-shrink-0">
            <Link
              to="/status"
              className="sm:hidden text-xs text-gray-400 hover:text-white transition-colors"
              data-testid="docs-nav-status-mobile"
            >
              Status
            </Link>
            <a
              href="/login"
              className="text-sm text-gray-300 hover:text-white transition-colors"
              data-testid="docs-nav-login"
            >
              Login
            </a>
            <Link
              to="/join-beta"
              className="px-4 py-2 rounded-full bg-phosphor-400 text-space-900 text-sm font-medium hover:bg-phosphor-500 transition-colors glow-green"
              data-testid="docs-nav-join-beta"
            >
              Join Beta
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default DocsNavigation;
